import { AdditionalOperation, RulesLogic } from 'json-logic-js'
import { adUnitAlias, adUnitAliasToPath, gameAlias, gameAliasToIdMap } from '../constants'

// @ts-expect-error 'import.meta' is handled by babel
const BACKGROUND_IMAGE = new URL('../images/wooden_tile_light_background.png', import.meta.url)
const NO_PAYMENT_DAYS = 30 // Ad should be displayed for users who didn't have payment for last XX days
const RETURNED_PLAYER_NO_ADS_DAYS = 7 // Ads should be disabled for XX days after inactivity threshold defined in the game
const MIN_LEVEL_ADS = 10 // Minimum level for displaying ad
const MIN_LEVEL_AD_BLOCKER = 10 // Minimum level for displaying ad blocker

export const DEFAULT_CONFIG = {
  backgroundImage: BACKGROUND_IMAGE,
  refreshTime: 30, // Seconds
  requirements: {
    minLevel: MIN_LEVEL_ADS,
    noPaymentDays: NO_PAYMENT_DAYS,
  },
}

export interface BannersConfig {
  gameId: gameAlias
  banners: {
    horizontal: {
      rules: {
        loginData: RulesLogic<AdditionalOperation>
        paymentData: RulesLogic<AdditionalOperation>
      }
    }
    vertical: {
      rules: {
        loginData: RulesLogic<AdditionalOperation>
        paymentData: RulesLogic<AdditionalOperation>
      }
    }
  }
  adblocker: {
    rules: RulesLogic<AdditionalOperation>
  }
  adSlotSettings: {
    unitPath: adUnitAlias
    defaultAdSize: [number, number]
    sizeMap: Map<[number, number], [number, number][]>
    htmlElements: {
      adPlaceholder: {
        id: string
        width: string | number
        height: string | number
        style: string
      }
      adContainer: {
        id: string
        style: string
      }
    }
  }[]
}

export type BannersConfigWithDefault = typeof DEFAULT_CONFIG & BannersConfig

export type bannerConditionType = keyof BannersConfigWithDefault['banners']['horizontal']['rules']

const DEFAULT_RULES: RulesLogic<AdditionalOperation>[] = [
  {
    isGGSNetwork: [{ var: ['networkId'] }, [1]],
  },
  {
    or: [
      { '==': [{ var: 'lastPaymentDate' }, 0] },
      {
        isExpired: [{ var: 'lastPaymentDate' }, NO_PAYMENT_DAYS],
      },
    ],
  },
  {
    // no ads for players with active subscription
    '!=': [{ var: 'hasSubscription' }, true],
  },
  {
    // for some partners we need to disable the horizontal banner
    '!': [
      {
        in: [
          { var: 'originUrl' },
          {
            cat: ['bigfarm.rtl.de ', 'bigfarm.rtl2-spiele.de ', 'empire.rtl.de ', 'empire.rtl2-spiele.de '],
          },
        ],
      },
    ],
  },
]

export const BigFarm: BannersConfig = {
  gameId: gameAliasToIdMap.BIGFARM,
  banners: {
    horizontal: {
      rules: {
        loginData: {
          and: [
            ...DEFAULT_RULES,
            {
              and: [
                {
                  or: [
                    {
                      /* live instances */
                      in: [
                        { var: 'instanceId' },
                        [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25],
                      ],
                    },
                    {
                      /* test instances */
                      '>=': [{ var: 'instanceId' }, 251],
                    },
                  ],
                },
                { '>=': [{ var: 'level' }, MIN_LEVEL_ADS] },
              ],
            },
          ],
        },
        paymentData: {
          or: [{ '==': [{ var: 'lastPaymentTime' }, 0] }, { isExpired: [{ var: 'lastPaymentTime' }, NO_PAYMENT_DAYS] }],
        },
      },
      // TODO: add adSlotSettings here as well
    },
    vertical: {
      rules: {
        loginData: {
          and: [
            ...DEFAULT_RULES,
            {
              and: [
                {
                  or: [
                    {
                      /* live instances */
                      in: [
                        { var: 'instanceId' },
                        [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25],
                      ],
                    },
                    {
                      /* test instances */
                      '>=': [{ var: 'instanceId' }, 251],
                    },
                  ],
                },
                { '>=': [{ var: 'level' }, MIN_LEVEL_ADS] },
              ],
            },
          ],
        },
        paymentData: {
          or: [{ '==': [{ var: 'lastPaymentTime' }, 0] }, { isExpired: [{ var: 'lastPaymentTime' }, NO_PAYMENT_DAYS] }],
        },
      },
    },
  },
  adblocker: {
    // if the following conditions are true, and the banners are available, then the adblocker is enabled
    rules: {
      and: [{ '>=': [{ var: 'level' }, MIN_LEVEL_AD_BLOCKER] }],
    },
  },
  adSlotSettings: [
    {
      // Horizontal banner
      unitPath: adUnitAliasToPath.BIGFARM_HORIZONTAL,
      defaultAdSize: [728, 90],
      sizeMap: new Map([
        [
          [1200, 700],
          [
            [970, 90],
            [728, 90],
          ],
        ],
        [[728, 700], [[728, 90]]],
        [[0, 0], []],
      ]),
      htmlElements: {
        adPlaceholder: {
          id: 'ad-placeholder-horizontal',
          width: 'calc(100% - 300px)',
          height: 100,
          // Width and height should match the desired banner size (number means pixels and string is for using %/vw/vh css units)
          // TODO: improve to reuse variables
          style: `
            width: calc(100% - 300px);
            height: 100px;
            position: absolute;
            top: 0px;
            display: block;
            min-width: 798px;
            min-height: 100px;
            align: center;`,
        },
        adContainer: {
          id: 'ad-container-horizontal',
          style: `
            width: fit-content;
            height: fit-content;
            display:inline-block;
            background: #fff;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            position: relative;
            max-height: 90px;`,
        },
      },
    },
    {
      // Vertical banner
      unitPath: adUnitAliasToPath.BIGFARM_VERTICAL,
      defaultAdSize: [160, 600],
      sizeMap: new Map([
        [
          [1100, 800],
          [
            [220, 800],
            [160, 600],
            [300, 600],
            [300, 250],
          ],
        ],
        [
          [1100, 600],
          [
            [160, 600],
            [300, 600],
            [300, 250],
          ],
        ],
        [[0, 0], []],
      ]),
      htmlElements: {
        adPlaceholder: {
          id: 'ad-placeholder-vertical',
          width: 300,
          height: '100%',
          // Width and height should match the desired banner size (number means pixels and string is for using %/vw/vh css units)
          // TODO: improve to reuse variables
          style: `
            width: 300px;
            height: 100%;
            position: absolute;
            right: 0px;
            top: 0px;
            display: block;
            min-width: 300px;
            min-height: 610px;
            align: center;`,
        },
        adContainer: {
          id: 'ad-container-vertical',
          style: `
            width: fit-content;
            height: fit-content;
            display:inline-block;
            background: #fff;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            position: relative;
            max-width: 300px;`,
        },
      },
    },
  ],
}

export const Empire: BannersConfig = {
  gameId: gameAliasToIdMap.EMPIRE,
  banners: {
    horizontal: {
      rules: {
        loginData: {
          and: [
            ...DEFAULT_RULES,
            {
              // Limiting ads for returned players (only Empire)
              showAdsReturnedPlayer: [{ var: 'dayWentBack' }, RETURNED_PLAYER_NO_ADS_DAYS],
            },
            {
              and: [
                {
                  or: [
                    {
                      /* live instances */
                      in: [
                        { var: 'instanceId' },
                        [
                          1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26,
                          27, 28, 29, 32, 33, 34, 35, 36, 37, 38, 43, 46,
                        ],
                      ],
                    },
                    {
                      /* test instances */
                      '>=': [{ var: 'instanceId' }, 251],
                    },
                  ],
                },
                { '>=': [{ var: 'level' }, MIN_LEVEL_ADS] },
              ],
            },
          ],
        },
        paymentData: {
          or: [{ '==': [{ var: 'LPD' }, 0] }, { isExpired: [{ var: 'LPD' }, NO_PAYMENT_DAYS] }],
        },
      },
    },
    vertical: {
      rules: {
        loginData: {
          and: [
            ...DEFAULT_RULES,
            {
              // Limiting ads for returned players (only Empire)
              showAdsReturnedPlayer: [{ var: 'dayWentBack' }, RETURNED_PLAYER_NO_ADS_DAYS],
            },
            {
              and: [
                {
                  or: [
                    {
                      /* live instances */
                      in: [
                        { var: 'instanceId' },
                        [
                          1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26,
                          27, 28, 29, 32, 33, 34, 35, 36, 37, 38, 43, 46,
                        ],
                      ],
                    },
                    {
                      /* test instances */
                      '>=': [{ var: 'instanceId' }, 251],
                    },
                  ],
                },
                { '>=': [{ var: 'level' }, MIN_LEVEL_ADS] },
              ],
            },
          ],
        },
        paymentData: {
          or: [{ '==': [{ var: 'LPD' }, 0] }, { isExpired: [{ var: 'LPD' }, NO_PAYMENT_DAYS] }],
        },
      },
    },
  },
  adblocker: {
    // if any of the following conditions are true, then the adblocker is enabled
    rules: {
      and: [{ '>=': [{ var: 'level' }, MIN_LEVEL_AD_BLOCKER] }],
    },
  },
  adSlotSettings: [
    {
      // Horizontal banner
      unitPath: adUnitAliasToPath.EMPIRE_HORIZONTAL,
      defaultAdSize: [728, 90],
      sizeMap: new Map([
        [
          [1200, 700],
          [
            [970, 90],
            [728, 90],
          ],
        ],
        [[728, 700], [[728, 90]]],
        [[0, 0], []],
      ]),
      htmlElements: {
        adPlaceholder: {
          id: 'ad-placeholder-horizontal',
          width: 'calc(100% - 300px)',
          height: 100,
          // Width and height should match the desired banner size (number means pixels and string is for using %/vw/vh css units)
          // TODO: improve to reuse variables
          style: `
            width: calc(100% - 300px);
            height: 100px;
            position: absolute;
            top: 0px;
            display: block;
            min-width: 798px;
            min-height: 100px;
            align: center;`,
        },
        adContainer: {
          id: 'ad-container-horizontal',
          style: `
            width: fit-content;
            height: fit-content;
            display:inline-block;
            background: #fff;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            position: relative;
            max-height: 90px;`,
        },
      },
    },
    {
      // Vertical banner
      unitPath: adUnitAliasToPath.EMPIRE_VERTICAL,
      defaultAdSize: [160, 600],
      sizeMap: new Map([
        [
          [1100, 800],
          [
            [220, 800],
            [160, 600],
            [300, 600],
            [300, 250],
          ],
        ],
        [
          [1100, 600],
          [
            [160, 600],
            [300, 600],
            [300, 250],
          ],
        ],
        [[0, 0], []],
      ]),
      htmlElements: {
        adPlaceholder: {
          id: 'ad-placeholder-vertical',
          width: 300,
          height: '100%',
          // Width and height should match the desired banner size (number means pixels and string is for using %/vw/vh css units)
          // TODO: improve to reuse variables
          style: `
            width: 300px;
            height: 100%;
            position: absolute;
            right: 0px;
            top: 0px;
            display: block;
            min-width: 300px;
            min-height: 610px;
            align: center;`,
        },
        adContainer: {
          id: 'ad-container-vertical',
          style: `
            width: fit-content;
            height: fit-content;
            display:inline-block;
            background: #fff;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            position: relative;
            max-width: 300px;`,
        },
      },
    },
  ],
}
